import { LoaderFunctionArgs, defer } from '@remix-run/node'
import { Await, useLoaderData, useNavigate } from '@remix-run/react'
import SignForm from '#app/components/common/signForm.tsx'
import { AttestationType, eventData, nft } from '#types/loreTypes.tsx'
import {
	getEventDataByEventCID,
	getMediaThumbnail,
} from '#app/server/db.server.ts'
import { Suspense, useEffect, useState } from 'react'
import { DynamicContext } from '@dynamic-labs/sdk-react-core'
import { Nft } from 'alchemy-sdk'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { formatString } from '#app/utils/misc.tsx'
import placeholder from '#images/video-placeholder-dark.jpg'
import { getAssetUrl } from '#app/utils/cloudinary.server.ts'

export const loader = async ({
	params,
	context,
	request,
}: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	try {
		if (
			typeof params.eventCID === 'string' &&
			typeof params.rollupCID === 'string'
		) {
			let rollupCID: string = params.rollupCID
			const nftData = await getEventDataByEventCID(params.eventCID)

			if (nftData.length === 0) {
				throw await redirectWithToast('/', {
					type: 'error',
					title: 'Invalid Event CID',
					description: 'Could not find any entries related to this Event CID.',
				})
			}
			const rollupCIDs = nftData.map(nftEntry => nftEntry.nftCID)
			const eventDataFromNFT = nftData[0] as eventData

			const mappedNFTs = (
				await Promise.all(
					nftData.map(async (nftEntry: eventData) => {
						try {
							const nftDataAlchemy = JSON.parse(nftEntry.metadata) as Nft
							if (!nftDataAlchemy) {
								console.error('Could not find metadata for ', nftEntry)
								return
							}
							// @ts-ignore
							let nftData: nft = { ...nftDataAlchemy }
							nftData.chain = nftEntry.chain
							if (!nftData.name) nftData.name = `#${nftData.tokenId}`
							if (!nftData.description)
								nftData.description = 'Description unavailable'
							// Remove all format from strings
							nftData.name = formatString(nftData.name)
							nftData.description = formatString(nftData.description)
							nftData.contentType = nftDataAlchemy.image.contentType
							if (nftData.contentType === 'video/mp4') {
								nftData.previewImage = placeholder
							} else {
								// Resize image with cloudinary
								nftData.previewImage = await getAssetUrl(
									getMediaThumbnail(nftDataAlchemy),
								)
							}
							return nftData
						} catch (e) {
							console.error(e)
						}
					}),
				)
			).filter(elem => elem != undefined)

			const pendingPin = {
				rollupCID: rollupCID,
				// @ts-ignore
				address: eventDataFromNFT.eventSigner || '',
				startDate: eventDataFromNFT.dateStart,
				endDate: eventDataFromNFT.dateEnd,
				name: eventDataFromNFT.eventTitle,
				description: eventDataFromNFT.eventDescription,
				location: eventDataFromNFT.eventLocation,
				url: eventDataFromNFT.eventLink,
				eventCID: eventDataFromNFT.eventCID,
				rollupCIDs: rollupCIDs,
				template: {
					name: 'Roll-up',
					slug: 'rollup',
					version: '1.0.0',
				},
			}

			const cacheHeaders = { 'Cache-Control': 'private, max-age=10000' }

			return defer(
				{
					nft: mappedNFTs,
					chain: null,
					origin: url.origin,
					atomicLoreUrl: url.origin,
					atomicSignUrl: import.meta.env.VITE_ATOMICSIGN_URL,
					pendingPin: pendingPin,
				},
				{ headers: cacheHeaders },
			)
		}
		return {
			nft: [],
			chain: null,
			origin: null,
			atomicLoreUrl: url.origin,
			atomicSignUrl: import.meta.env.VITE_ATOMICSIGN_URL,
		}
	} catch (e) {
		console.error(e)
		return {
			nft: null,
			chain: null,
			origin: null,
			atomicLoreUrl: url.origin,
			atomicSignUrl: import.meta.env.VITE_ATOMICSIGN_URL,
		}
	}
}

export default function rollupPage() {
	const { nft, chain, origin, atomicLoreUrl, atomicSignUrl, pendingPin } =
		useLoaderData<{
			nft: nft[]
			chain: string
			origin: string
			atomicLoreUrl: string
			atomicSignUrl: string
			pendingPin: any
			// eventData: any
		}>()

	const [signedEntry, setSignedEntry] = useState<AttestationType | null>(null)
	useEffect(() => {
		if (signedEntry) {
			// TODO redirect to success state?
		}
	}, [signedEntry])

	return (
		<div className="mx-auto flex max-w-[1440px] justify-center px-4 py-16 md:px-12 lg:px-28">
			<Suspense
				fallback={
					!DynamicContext._currentValue ? (
						<div></div>
					) : (
						<SignForm
							nft={[]}
							chain={chain}
							origin={origin}
							atomicLoreUrl={atomicLoreUrl}
							atomicSignUrl={atomicSignUrl}
							template={'rollup'}
							pendingPin={pendingPin}
							setSignedEntry={setSignedEntry}
						/>
					)
				}
			>
				<Await resolve={nft}>
					{nft => {
						if (nft && nft.length > 0 && DynamicContext._currentValue) {
							return (
								<SignForm
									nft={nft}
									chain={chain}
									origin={origin}
									atomicLoreUrl={atomicLoreUrl}
									atomicSignUrl={atomicSignUrl}
									template={'rollup'}
									pendingPin={pendingPin}
									setSignedEntry={setSignedEntry}
								/>
							)
						} else {
							return <h1>NO NFTs</h1>
						}
					}}
				</Await>
			</Suspense>
		</div>
	)
}
